import React, { useState, useEffect } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './collapsible';
import { Card, CardHeader, CardContent } from './card';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Wrapper réutilisable pour rendre chaque bloc de sidebar collapsible et indépendant.
 * Mémorise son état via localStorage (clé `collapsible_state_{id}`).
 *
 * Props:
 *  - id (string, required) — Identifiant unique persisté en localStorage
 *  - title (string, required)
 *  - icon (lucide-react component, optional)
 *  - defaultOpen (bool, default true)
 *  - headerBadge (ReactNode optional) — badge ou indicateur au header (statut, compteur, cadenas...)
 *  - summaryText (string optional) — texte affiché à droite quand le bloc est fermé
 *  - className / headerClassName — overrides
 */
export function CollapsibleCard({
  id,
  title,
  icon: Icon,
  defaultOpen = true,
  headerBadge,
  summaryText,
  children,
  className = '',
  headerClassName = '',
}) {
  const storageKey = `collapsible_state_${id}`;
  const [isOpen, setIsOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved !== null ? JSON.parse(saved) : defaultOpen;
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(isOpen));
    } catch {}
  }, [isOpen, storageKey]);

  return (
    <Card className={`border overflow-hidden ${className}`} data-testid={`collapsible-card-${id}`}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="p-0 border-b border-transparent data-[state=open]:border-gray-100">
          <div className={`w-full flex items-center justify-between p-3 ${headerClassName}`}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 overflow-hidden flex-1 cursor-pointer hover:opacity-80 transition-opacity text-left"
                data-testid={`collapse-trigger-${id}`}
              >
                {Icon && <Icon className="w-4 h-4 shrink-0" />}
                <span className="text-sm font-semibold truncate">{title}</span>
                {!isOpen && summaryText && (
                  <span className="text-[11px] text-muted-foreground hidden md:inline-block max-w-[180px] truncate ml-2">
                    {summaryText}
                  </span>
                )}
              </button>
            </CollapsibleTrigger>
            <div className="flex items-center gap-2 shrink-0 ml-2">
              {headerBadge && <div className="shrink-0">{headerBadge}</div>}
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  aria-label={isOpen ? 'Réduire' : 'Développer'}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                >
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </button>
              </CollapsibleTrigger>
            </div>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="p-3">{children}</CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export default CollapsibleCard;
